/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-08 10:30:31                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2024-11-08 11:04:32                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * Styles
  * Prospect: prospect view
  * useEffect: get prospect data
  * useEffect: set breadcrumb
*/

/* Imports */
import { t } from "i18next";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, Typography } from "@material-tailwind/react";
import toast from "react-hot-toast";
import { showApiErrorToast } from "../../services/api/error";
/***/

/* Services */
import { api } from "../../services/api/api";
import { validator } from "../../services/form-validator/form-validator";
import { buildProspectPayload } from "../../services/prospect/prospect";
/***/

/* Components */
import Loading from "../../components/loading/Loading";
import { Breadcrumb } from "../../components/breadcrumb/Breadcrumb";
import TechnicianDisplay from "../../components/data/technician/TechnicianDisplay";
import CustomerDisplay from "../../components/data/customer/CustomerDisplay";
import ContactDisplay from "../../components/data/contact/ContactDisplay";
import AddressDisplay from "../../components/data/address/AddressDisplay";
import DateDisplay from "../../components/data/date/DateDisplay";
import EditCreateDrawer from "../../components/edit-create-drawer/EditCreateDrawer";
import ProspectForm, { ProspectFormValidator } from "../../components/data/prospect/ProspectForm";
/***/

/* Styles */
import "./Prospect.scss";
/***/

/**
* Prospect: prospect view
* display prospect details 
*/
export function Prospect() {
  const { id } = useParams();
  const [prospect, setProspect] = useState<EasyTech.Prospect>();
  const [path, setPath] = useState<{ name: string, path: string }[]>([]);

  /**
  * useEffect: get prospect data
  * reload on id change 
  */
  useEffect(() => {
    api.get(`users/prospects/${id}`)
      .then((ret) => setProspect(ret as EasyTech.Prospect))
      .catch((err) => console.error(err));
  }, [id]);
  /***/

  /**
  * useEffect: set breadcrumb
  * reload on prospect change
  */
  useEffect(() => {
    if (prospect) {
      setPath([{
        name: "visits",
        path: "/visits"
      }, {
        name: ` ${prospect.ref}`,
        path: `/visits/${prospect.id}`
      }]);
    }
  }, [prospect]);
  /***/

  const handleProspectEdit = (data: EasyTech.Prospect): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (validator.isFormValid(data, ProspectFormValidator)) {
        api.put(`users/prospects/${data.id}`, buildProspectPayload(data))
          .then((ret) => {
            toast.success(t("prospect:notifications:update:success"));
            setProspect((prev) => ({
              ...(prev || data),
              ...(ret as EasyTech.Prospect),
            }));
            return resolve();
          }).catch((err) => {
            console.error(err);
            showApiErrorToast(err);
            return reject();
          });
      } else {
        const fieldNames = {
          customer: t("common:customer"),
          contact: t("common:contact"),
          technician: t("common:technician"),
          date: t("common:date:label")
        };

        const errorMessage = validator.getFormErrorMessage(data, ProspectFormValidator, fieldNames, t);
        toast.error(errorMessage);
        return reject();
      }
    });
  };

  return (
    <div className="prospect-details">
      {prospect ?
        <>
          <Breadcrumb path={path} />

          <Typography variant="h2">
            {t("prospect:title")} - {prospect.ref}
          </Typography>

          <div className="flex justify-between items-center">
            <Typography variant="h4" className="sub-title">
              {t("Informations")}
            </Typography>

            <div className="flex gap-2">
              <EditCreateDrawer<EasyTech.Prospect>
                data={prospect}
                FormComponent={ProspectForm}
                isNotCreatable={true}
                onSubmit={handleProspectEdit}
                entityName="prospect"
                editButtonText={t("common:edit")}
              />
            </div>
          </div>

          <Card className="details-card">
            <div className="field">
              <label>{t("common:ref:label")}</label>
              <Typography>{prospect.ref}</Typography>
            </div>

            <div className="field">
              <label>{t("common:date:label")}</label>
              <DateDisplay date={new Date(prospect.date)} />
            </div>

            <div className="field">
              <label>{t("common:technician")}</label>
              <TechnicianDisplay technician={prospect.technician} />
            </div>

            <div className="field">
              <label>{t("date:hour")}</label>
              <Typography>{prospect.hour || "-"}</Typography>
            </div>

            <div className="field">
              <label>{t("common:customer")}</label>
              <CustomerDisplay customer={prospect.customer} />
            </div>

            <div className="field">
              <label>{t("common:contact")}</label>
              <ContactDisplay contact={prospect.contact} />
            </div>

            <div className="field col-1 col-span-2">
              <label>{t("common:address")}</label>
              {prospect.address ?
                <AddressDisplay address={prospect.address} />
              :
                <Typography>-</Typography>
              }
            </div>

            <div className="field col-1 col-span-2">
              <label>{t("form:prospect:remarks")}</label>
              <Typography>{prospect.remarks}</Typography>
            </div>
          </Card>
        </>
        : <Loading />}
    </div>
  );
}
/***/
